import Vue from 'vue'
import { createAuth0Client } from '@auth0/auth0-spa-js'

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname)

let instance

const getAuth0Instance = () => instance

const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  returnTo = `${window.location.origin}/`,
  ...options
}) => {
  instance = new Vue({
    data () {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        error: null,
        returnTo,
      }
    },
    computed: {
      isReady () {
        return !this.loading
      },
      normalizedUser () {
        const keyEndsWith = s => Object.keys(this.user).find(key => key.endsWith(s))
        const canHaveTrial = this.user[keyEndsWith('can_have_trial')]
        const roles = this.user[keyEndsWith('roles')]
        const identity = this.user[keyEndsWith('identities')]
        const useMfa = this.user[keyEndsWith('use_mfa')]
        const forceMfa = this.user[keyEndsWith('force_mfa')]
        const userWithoutCustomClaims = Object.keys(this.user)
          .filter(key => !key.startsWith('http'))
          .reduce((obj, key) => {
            obj[key] = this.user[key]
            return obj
          }, {})
        return {
          ...userWithoutCustomClaims,
          canHaveTrial,
          roles,
          identity: identity ? identity[0] : {},
          useMfa,
          forceMfa,
        }
      },
    },
    async created () {
      this.auth0Client = await createAuth0Client({
        domain: options.domain,
        clientId: options.clientId,
        authorizationParams: {
          audience: options.audience,
          redirect_uri: redirectUri,
        },
        // use cookies and store the token in the local storage in e2e tests
        // this makes login for e2e test much easier
        cacheLocation: process.env.VUE_APP_AUTH0_CACHE_LOCATION,
        useRefreshTokens: !process.env.VUE_APP_AUTH0_USE_COOKIES,
      })

      try {
        if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
          const { appState } = await this.auth0Client.handleRedirectCallback()
          this.error = null
          onRedirectCallback(appState)
        }
      } catch (e) {
        this.error = e
      } finally {
        this.isAuthenticated = await this.auth0Client.isAuthenticated()
        this.user = await this.auth0Client.getUser()
        this.loading = false
      }
    },
    methods: {
      loginWithRedirect (opt) {
        return this.auth0Client.loginWithRedirect(opt)
      },
      getTokenSilently (opt) {
        return this.auth0Client.getTokenSilently(opt)
      },
      logout (opt) {
        this.isAuthenticated = false
        return this.auth0Client.logout({
          logoutParams: {
            returnTo: this.returnTo,
          },
          ...opt,
        })
      },
      signupWithRedirect () {
        return this.auth0Client.loginWithRedirect({
          authorizationParams: {
            initial_screen: 'signUp',
          },
        })
      },
    },
  })

  return instance
}

const Auth0Plugin = {
  install (Vue, options) {
    Vue.prototype.$auth0 = useAuth0(options || {})
    if (options?.component) {
      Vue.component('CAccount', options.component)
    }
  },
}

export {
  Auth0Plugin,
  getAuth0Instance,
}
